/**
 * FormPrefillProvider (Phase 19D: Form Pre-fill & Guided Creation)
 *
 * React context that stores pending form pre-fill data from chatbot navigation.
 * Form pages consume and clear prefill data via useFormPrefill() hook.
 *
 * @module FormPrefillProvider
 */

'use client';

import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

// ============================================================================
// Types
// ============================================================================

interface FormPrefillData {
  /** Entity type (e.g., "order_core") */
  entityType?: string;
  /** Field name → value mappings */
  fields: Record<string, string>;
  /** Timestamp when set (for auto-clear after stale timeout) */
  setAt: number;
}

interface FormPrefillContextValue {
  /** Current pending prefill data (null if none) */
  prefillData: FormPrefillData | null;
  /** Set new prefill data (called by chatbot navigation handler) */
  setPrefillData: (fields: Record<string, string>, entityType?: string) => void;
  /** Consume and clear prefill data (called by form pages) */
  consumePrefillData: () => Record<string, string> | null;
  /** Check if prefill data is available without consuming */
  hasPrefillData: () => boolean;
}

// ============================================================================
// Constants
// ============================================================================

/** Auto-clear prefill data after 5 minutes (prevents stale data) */
const PREFILL_STALE_TIMEOUT_MS = 5 * 60 * 1000;

// ============================================================================
// Context
// ============================================================================

const FormPrefillContext = createContext<FormPrefillContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export interface FormPrefillProviderProps {
  children: React.ReactNode;
}

/**
 * FormPrefillProvider component.
 *
 * Stores form pre-fill data from chatbot navigation commands.
 * Data is consumed once by the target form and automatically cleared.
 *
 * @example
 * <FormPrefillProvider>
 *   <ChatbotProvider>
 *     <App />
 *   </ChatbotProvider>
 * </FormPrefillProvider>
 */
export function FormPrefillProvider({ children }: FormPrefillProviderProps) {
  const [prefillData, setPrefillDataState] = useState<FormPrefillData | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setPrefillData = useCallback((fields: Record<string, string>, entityType?: string) => {
    // Clear any existing stale timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setPrefillDataState({
      entityType,
      fields,
      setAt: Date.now(),
    });

    // Auto-clear after timeout to prevent stale data
    timeoutRef.current = setTimeout(() => {
      setPrefillDataState(null);
    }, PREFILL_STALE_TIMEOUT_MS);
  }, []);

  const consumePrefillData = useCallback((): Record<string, string> | null => {
    if (!prefillData) return null;

    // Check if data is stale
    if (Date.now() - prefillData.setAt > PREFILL_STALE_TIMEOUT_MS) {
      setPrefillDataState(null);
      return null;
    }

    const fields = { ...prefillData.fields };

    // Clear after consumption (one-time use)
    setPrefillDataState(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    return fields;
  }, [prefillData]);

  const hasPrefillData = useCallback((): boolean => {
    if (!prefillData) return false;
    return Date.now() - prefillData.setAt <= PREFILL_STALE_TIMEOUT_MS;
  }, [prefillData]);

  const value: FormPrefillContextValue = {
    prefillData,
    setPrefillData,
    consumePrefillData,
    hasPrefillData,
  };

  return <FormPrefillContext.Provider value={value}>{children}</FormPrefillContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access form pre-fill data.
 *
 * @returns FormPrefillContextValue or a no-op implementation if outside provider
 *
 * @example
 * const { consumePrefillData, hasPrefillData } = useFormPrefill();
 *
 * useEffect(() => {
 *   if (hasPrefillData()) {
 *     const fields = consumePrefillData();
 *     if (fields) {
 *       Object.entries(fields).forEach(([key, value]) => form.setValue(key, value));
 *     }
 *   }
 * }, []);
 */
export function useFormPrefill(): FormPrefillContextValue {
  const context = useContext(FormPrefillContext);

  // Return no-op implementation if outside provider (graceful degradation)
  if (!context) {
    return {
      prefillData: null,
      setPrefillData: () => {},
      consumePrefillData: () => null,
      hasPrefillData: () => false,
    };
  }

  return context;
}

// ============================================================================
// Exports
// ============================================================================

export { FormPrefillContext };
export type { FormPrefillData, FormPrefillContextValue };
